import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { clearLessons } from '../../../Redux/lessonsSlice';
import { selectSlotsByStudent } from '../../../Redux/selectors';
import { useTranslation } from '../../../i18n/useTranslation';
import type { LessonModel } from '../../../Models/LessonModel';
import { LessonStatus } from '../../../Models/enums';
import { displayNameOf } from '../../../Utils/DisplayNameUtil';
import { formatDate } from '../../../Utils/DateUtil';
import { lessonVisualState } from '../../../Utils/LessonStatusUtil';
import { routes } from '../../../Utils/routes';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { ConfirmDialog } from '../../SharedArea/ConfirmDialog/ConfirmDialog';
import { LessonStatusSheet } from '../Lessons/LessonStatusSheet';
import { DebtReminderAction } from '../Students/DebtReminderAction/DebtReminderAction';
import type { StudentModel } from '../../../Models/StudentModel';
import './Payments.css';
import { useDisplayName } from '../../../Utils/useDisplayName';

type StatusFilter = 'all' | 'paid' | 'unpaid' | 'scheduled' | 'cancelled';
const PAGE_SIZE = 10;

/** The financial ledger: one row per lesson, matching the original app. */
export function Payments() {
  const { tr, language, direction } = useTranslation();
  const dispatch = useAppDispatch();
  const lessons = useAppSelector(state => state.lessons.items);
  const students = useAppSelector(state => state.students.items);
  const slotsByStudent = useAppSelector(selectSlotsByStudent);
  const state = useAppSelector(store => store.lessons.state);
  const error = useAppSelector(store => store.lessons.error);
  // ?student= is how the roster's history button arrives here. It seeds the
  // search box rather than being a filter of its own, so the tutor can widen
  // it from where they landed instead of having to clear something hidden.
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('student') ?? '');
  const [month, setMonth] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<LessonModel | null>(null);
  const [clearing, setClearing] = useState(false);

  /**
   * Every lesson a student currently owes for, whatever the filter is showing.
   *
   * A reminder is about the debt, not about the row it was sent from: chasing
   * one lesson while three others are outstanding is how a tutor ends up
   * sending four messages for one conversation.
   */
  const owedByStudent = useMemo(() => {
    const map = new Map<string, LessonModel[]>();
    for (const lesson of lessons) {
      const visual = lessonVisualState(lesson, slotsByStudent.get(lesson.student) ?? []);
      if (visual !== 'is-unpaid' && visual !== 'is-cancelled-charged') continue;
      const bucket = map.get(lesson.student);
      if (bucket) bucket.push(lesson);
      else map.set(lesson.student, [lesson]);
    }
    return map;
  }, [lessons, slotsByStudent]);

  const studentsByName = useMemo(
    () => new Map(students.map(student => [student.name, student])),
    [students]
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase(language === 'he' ? 'he-IL' : 'en-US');
    const displayNameEn = (name: string) => displayNameOf(name, students, 'en');
    return lessons
      .filter(lesson => {
        // Either spelling, whatever the interface language, like the roster search.
        if (needle && !`${lesson.student} ${displayNameEn(lesson.student)}`.toLocaleLowerCase(language === 'he' ? 'he-IL' : 'en-US').includes(needle)) return false;
        if (month && !lesson.date.startsWith(month)) return false;
        if (status === 'all') return true;
        const visual = lessonVisualState(lesson, slotsByStudent.get(lesson.student) ?? []);
        if (status === 'paid') return visual === 'is-paid';
        if (status === 'unpaid') return visual === 'is-unpaid' || visual === 'is-cancelled-charged';
        if (status === 'scheduled') return visual === 'is-scheduled';
        return visual === 'is-cancelled-charged' || visual === 'is-cancelled-free';
      })
      .slice()
      .sort((a, b) => b.date.localeCompare(a.date) || (b.time ?? '').localeCompare(a.time ?? ''));
  }, [lessons, query, month, status, slotsByStudent, language, students]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => setPage(1), [query, month, status]);
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

  if (state === 'loading' || state === 'idle') return <Spinner />;
  if (state === 'failed') return <ErrorState code={error} />;

  function reset() {
    setQuery('');
    setMonth('');
    setStatus('all');
  }

  return (
    <section className="payments" dir={direction}>
      <section className="payments-toolbar" aria-label={tr('paymentsFilterLabel')}>
        <label className="payments-search">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder={tr('paymentsSearchPlaceholder')} aria-label={tr('paymentsSearchPlaceholder')} />
        </label>
        <input className="payments-month" type="month" value={month} onChange={event => setMonth(event.target.value)} aria-label={tr('calendarMonth')} />
        <select className="payments-status-filter" value={status} onChange={event => setStatus(event.target.value as StatusFilter)} aria-label={tr('allStatuses')}>
          <option value="all">{tr('allStatuses')}</option>
          <option value="paid">{tr('paid')}</option>
          <option value="unpaid">{tr('unpaid')}</option>
          <option value="scheduled">{tr('scheduled')}</option>
          <option value="cancelled">{tr('cancelledStatuses')}</option>
        </select>
        <button type="button" className="payments-reset" onClick={reset}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8M3 3v5h5" /></svg>
          {tr('resetFilters')}
        </button>
      </section>

      <section className="payments-actions">
        <button type="button" className="ghost-button" onClick={() => window.print()}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7 9V3h10v6M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2M7 14h10v7H7z" /></svg>
          {tr('printList')}
        </button>
        <button type="button" className="ghost-button is-destructive" onClick={() => setClearing(true)} disabled={lessons.length === 0}>
          {tr('clearAllLessons')}
        </button>
        <Link className="primary-button payments-add-btn" to={`${routes.lessons}?add=1`} aria-label={tr('addLesson')}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" /></svg>
          <span>{tr('addLesson')}</span>
        </Link>
      </section>

      {filtered.length === 0
        ? <EmptyState title={tr('noLessons')} hint={tr('noLessonsHint')} />
        : (
          <section className="payments-ledger">
            <div className="payments-feed">
              {visible.map(lesson => {
                const visual = lessonVisualState(lesson, slotsByStudent.get(lesson.student) ?? []);
                const student = studentsByName.get(lesson.student);
                const owed = owedByStudent.get(lesson.student);
                return (
                  <PaymentRow
                    key={lesson.id}
                    lesson={lesson}
                    visual={visual}
                    language={language}
                    onEdit={setEditing}
                    {...(student && owed ? { debtor: student, owed } : {})}
                  />
                );
              })}
            </div>
            <div className="payments-print-feed" aria-hidden="true">
              {filtered.map(lesson => {
                const visual = lessonVisualState(lesson, slotsByStudent.get(lesson.student) ?? []);
                return <PaymentRow key={`print-${lesson.id}`} lesson={lesson} visual={visual} language={language} onEdit={() => {}} />;
              })}
            </div>
            <footer className="payments-pagination" aria-label={tr('navPayments')}>
              <span>{tr('paymentsShowing', filtered.length)}</span>
              <div>
                <button type="button" onClick={() => setPage(current => Math.max(1, current - 1))} disabled={page === 1} aria-label={tr('previousPage')}>
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
                </button>
                <bdi>{tr('pageStatus', page, pageCount)}</bdi>
                <button type="button" onClick={() => setPage(current => Math.min(pageCount, current + 1))} disabled={page === pageCount} aria-label={tr('nextPage')}>
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m15 6-6 6 6 6" /></svg>
                </button>
              </div>
            </footer>
          </section>
        )}

      {editing && <LessonStatusSheet lesson={editing} onClose={() => setEditing(null)} />}

      {clearing && (
        <ConfirmDialog
          title={tr('clearAllLessonsTitle')}
          // Said in full, because the filter on screen makes this look narrower
          // than it is: it deletes every lesson, not the ones being shown.
          body={tr('clearAllLessonsBody')}
          confirmLabel={tr('delete')}
          destructive
          onCancel={() => setClearing(false)}
          onConfirm={() => {
            void dispatch(clearLessons());
            setClearing(false);
          }}
        />
      )}
    </section>
  );
}

function PaymentRow({ lesson, visual, language, onEdit, debtor, owed }: {
  lesson: LessonModel;
  visual: string;
  language: 'he' | 'en';
  onEdit: (lesson: LessonModel) => void;
  /** Present only when this row's student currently owes money. */
  debtor?: StudentModel;
  owed?: readonly LessonModel[];
}) {
  const { tr } = useTranslation();
  const displayName = useDisplayName();
  return (
    <article className={`payment-row ${visual}`}>
      <span className="payment-avatar" aria-hidden="true">{displayName(lesson.student).trim().charAt(0)}</span>
      <div className="payment-main">
        <strong className="payment-student">{displayName(lesson.student)}</strong>
        <span className="payment-date"><bdi>{formatDate(lesson.date, language)}{lesson.time ? ` · ${lesson.time}` : ''}</bdi></span>
        {lesson.receipt && <Link className="payment-receipt-mark" to={routes.receipts}>{tr('receiptTitle')} <bdi>{lesson.receipt.receiptNumber ?? tr('receiptDraft')}</bdi></Link>}
      </div>
      <MoneyText amount={lesson.amount} className="payment-amount" />
      <button type="button" className={`payment-status ${visual}`} onClick={() => onEdit(lesson)} aria-label={tr('editPaymentStatus')}>
        <span className="payment-status-dot" aria-hidden="true" />
        <span>{tr(statusKey(lesson, visual))}</span>
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {debtor && owed && owed.length > 0 && (
        <div className="payment-reminder">
          <DebtReminderAction student={debtor} lessons={owed} />
        </div>
      )}
    </article>
  );
}

function statusKey(lesson: LessonModel, visual: string) {
  if (visual === 'is-scheduled') return 'scheduled' as const;
  if (lesson.status === LessonStatus.Paid) return 'paid' as const;
  if (lesson.status === LessonStatus.CancelledCharged) return 'stCancelledCharged' as const;
  if (lesson.status === LessonStatus.CancelledFree) return 'stCancelledFree' as const;
  return 'unpaid' as const;
}
